// Respiratory algorithms — v2.1 collaboration prototype.
const arf = {
  id: "arf",
  specialty: "respiratory",
  available: true,
  title: { id: "Pendekatan Gagal Napas Akut", en: "Approach to Acute Respiratory Failure" },
  desc: { id: "Pendekatan awal", en: "Initial approach" },
  meta: {
    id: "Kerangka umum, konsisten dengan guideline ARF/ARDS terkini · Direview terakhir: Jul 2026",
    en: "General framework consistent with current ARF/ARDS guidelines · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Nilai ABC, oksimetri, EKG, akses IV. Oksigen bila hipoksia — hindari hiperoksia berlebihan.",
            en: "Assess ABC, pulse oximetry, ECG, IV access. Oxygen if hypoxic — avoid excessive hyperoxia.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Klasifikasi jenis gagal napas (analisis gas darah):",
            en: "Classify type of respiratory failure (blood-gas analysis):",
          },
          bodyExtra: {
            id: "Tipe 1 (hipoksemik) — curigai ARDS, pneumonia, edema paru, emboli paru. Tipe 2 (hiperkapnik) — curigai eksaserbasi PPOK, depresi napas sentral, kelemahan otot napas.",
            en: "Type 1 (hypoxaemic) — suspect ARDS, pneumonia, pulmonary oedema, pulmonary embolism. Type 2 (hypercapnic) — suspect COPD exacerbation, central respiratory depression, respiratory muscle weakness.",
          },
        },
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: { id: "Perlu dukungan ventilasi?", en: "Need ventilatory support?" },
          bodyExtra: {
            id: "Distres ringan-sedang, kesadaran baik → coba HFNC atau NIV (CPAP/BiPAP). Distres berat / tidak mampu proteksi jalan napas / gagal NIV → intubasi (lihat Rapid Sequence Intubation).",
            en: "Mild–moderate distress, alert → try HFNC or NIV (CPAP/BiPAP). Severe distress / cannot protect airway / NIV failure → intubate (see Rapid Sequence Intubation).",
          },
        },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Kerangka pendekatan umum — detail spesifik ada di algoritma masing-masing penyakit (Asma, PPOK, ARDS).",
        en: "General approach — disease-specific detail lives in individual algorithms (Asthma, COPD, ARDS).",
      },
    },
  ],
};

const asma = {
  id: "asma",
  specialty: "respiratory",
  available: true,
  title: {
    id: "Asma Eksaserbasi Berat / Status Asmatikus",
    en: "Severe Asthma Exacerbation / Status Asthmaticus",
  },
  desc: { id: "Status asmatikus", en: "Status asthmaticus" },
  meta: {
    id: "Sumber: GINA (Global Initiative for Asthma) 2025 · Direview terakhir: Jul 2026",
    en: "Source: GINA (Global Initiative for Asthma) 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Nilai derajat keparahan. Oksigen bila perlu, target SpO₂ ≥94%.",
            en: "Assess severity. Oxygen as needed, target SpO₂ ≥94%.",
          },
        },
        {
          badge: "2",
          eyebrow: { id: "Lini Pertama (jam pertama)", en: "First-line (first hour)" },
          body: { id: "", en: "" },
          list: {
            id: [
              "SABA dosis tinggi, ulang tiap 20 menit",
              "Kortikosteroid sistemik sedini mungkin",
              "Ipratropium bromide dikombinasi SABA bila berat",
            ],
            en: [
              "High-dose SABA, repeat every 20 min",
              "Systemic corticosteroid as early as possible",
              "Ipratropium bromide combined with SABA if severe",
            ],
          },
        },
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Respons tidak adekuat / eksaserbasi berat-mengancam jiwa?",
            en: "Inadequate response / life-threatening exacerbation?",
          },
          bodyExtra: {
            id: "Ya → Magnesium sulfat IV (bukan nebulisasi — tidak lagi direkomendasikan GINA 2025).",
            en: "Yes → IV magnesium sulfate (not nebulised — no longer recommended by GINA 2025).",
          },
        },
      ],
    },
    {
      type: "panel",
      heading: { id: "Tanda Mengancam Jiwa", en: "Life-threatening Signs" },
      tags: [
        { id: "Kesadaran menurun", en: "Reduced consciousness" },
        { id: "Silent chest", en: "Silent chest" },
        { id: "Sianosis", en: "Cyanosis" },
        { id: "Kelelahan otot napas", en: "Respiratory muscle fatigue" },
      ],
      footnote: {
        id: "Persiapkan kemungkinan intubasi — hati-hati, berisiko tinggi (barotrauma, hipotensi pasca-intubasi akibat air-trapping).",
        en: "Prepare for possible intubation — high risk (barotrauma, post-intubation hypotension from air-trapping).",
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Alat bantu edukasi & referensi cepat, bukan pengganti penilaian klinis. Dosis detail ada di menu Drug & Fluid.",
        en: "Educational quick reference only, not a substitute for clinical judgement. Full dose details are in the Drug & Fluid menu.",
      },
    },
  ],
};

const ppok = {
  id: "ppok",
  specialty: "respiratory",
  available: true,
  title: { id: "Eksaserbasi PPOK (COPD)", en: "COPD Exacerbation" },
  desc: { id: "", en: "" },
  meta: {
    id: "Sumber: GOLD 2025–2026 · Direview terakhir: Jul 2026",
    en: "Source: GOLD 2025–2026 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Nilai keparahan. Oksigen terkontrol (target SpO₂ 88–92%, sesuaikan baseline pasien). Bronkodilator kerja pendek (SABA + SAMA).",
            en: "Assess severity. Controlled oxygen (target SpO₂ 88–92%, adapt to patient baseline). Short-acting bronchodilators (SABA + SAMA).",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: { id: "Derajat eksaserbasi?", en: "Exacerbation severity?" },
        },
      ],
    },
    {
      type: "quad",
      cells: [
        {
          title: { id: "Ringan", en: "Mild" },
          desc: { id: "Bronkodilator saja, rawat jalan", en: "Bronchodilator only, outpatient" },
          accent: "sky",
        },
        {
          title: { id: "Sedang", en: "Moderate" },
          desc: { id: "+ kortikosteroid sistemik ~5 hari", en: "+ systemic corticosteroid ~5 days" },
          accent: "teal",
        },
        {
          title: { id: "Berat", en: "Severe" },
          desc: {
            id: "Rawat inap, O2 terkontrol, pertimbangkan NIV dini",
            en: "Inpatient, controlled O₂, consider early NIV",
          },
          accent: "danger",
        },
      ],
    },
    {
      type: "panel",
      heading: { id: "Kriteria Antibiotik (bukan untuk semua eksaserbasi)", en: "Antibiotic Criteria (not for every exacerbation)" },
      tags: [
        { id: "Peningkatan purulensi sputum", en: "Increased sputum purulence" },
        { id: "Bukti infeksi bakteri", en: "Evidence of bacterial infection" },
        { id: "Butuh dukungan ventilasi", en: "Requiring ventilatory support" },
      ],
    },
    {
      type: "flowTrack",
      steps: [
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Gagal napas hiperkapnik menetap meski NIV optimal / kontraindikasi NIV?",
            en: "Persistent hypercapnic respiratory failure despite optimal NIV / contraindication to NIV?",
          },
          bodyExtra: {
            id: "Ya → intubasi & ventilasi mekanik invasif.",
            en: "Yes → intubation & invasive mechanical ventilation.",
          },
        },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Sesuaikan target saturasi dengan baseline pasien (risiko retensi CO2 kronik) dan protokol RS setempat.",
        en: "Adjust saturation target to patient baseline (chronic CO₂ retention risk) and local hospital protocol.",
      },
    },
  ],
};

const ards = {
  id: "ards",
  specialty: "respiratory",
  available: true,
  title: { id: "ARDS (Acute Respiratory Distress Syndrome)", en: "ARDS (Acute Respiratory Distress Syndrome)" },
  desc: { id: "Kriteria Berlin & tata laksana", en: "Berlin criteria & management" },
  meta: {
    id: "Sumber: ATS/ESICM Clinical Practice Guideline 2024, Definisi Global ARDS 2024 · Direview terakhir: Jul 2026",
    en: "Source: ATS/ESICM Clinical Practice Guideline 2024, 2024 Global Definition of ARDS · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Diagnosis", en: "Diagnosis" },
          body: {
            id: "Onset akut (<1 minggu), infiltrat bilateral, hipoksemia. Definisi global 2024 memperluas kriteria — pasien HFNC/masker oksigen (tanpa intubasi) juga bisa didiagnosis.",
            en: "Acute onset (<1 week), bilateral infiltrates, hypoxaemia. The 2024 global definition broadens criteria — patients on HFNC/oxygen mask (without intubation) can also be diagnosed.",
          },
        },
        {
          badge: "2",
          eyebrow: { id: "Ventilasi Protektif (bila terintubasi)", en: "Protective Ventilation (if intubated)" },
          body: {
            id: "Volume tidal 4–8 mL/kg berat badan prediksi. Batasi tekanan plateau <30 cmH2O. Hindari recruitment maneuver tekanan tinggi berkepanjangan.",
            en: "Tidal volume 4–8 mL/kg predicted body weight. Keep plateau pressure <30 cmH₂O. Avoid prolonged high-pressure recruitment manoeuvres.",
          },
        },
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "ARDS sedang-berat (PaO2/FiO2 <150)?",
            en: "Moderate–severe ARDS (PaO₂/FiO₂ <150)?",
          },
          bodyExtra: {
            id: "Ya → posisi pronasi, sesi >12 jam/hari (idealnya ≥16 jam berturut-turut). Tidak → lanjutkan ventilasi protektif standar.",
            en: "Yes → prone positioning, sessions >12 h/day (ideally ≥16 h continuous). No → continue standard protective ventilation.",
          },
        },
      ],
    },
    {
      type: "panel",
      heading: { id: "Terapi Tambahan", en: "Adjunctive Therapies" },
      tags: [
        { id: "Dipertimbangkan: kortikosteroid", en: "Consider: corticosteroids" },
        { id: "Dipertimbangkan: VV-ECMO (kasus berat terpilih)", en: "Consider: VV-ECMO (selected severe cases)" },
        { id: "Tidak rutin: HFOV", en: "Not routine: HFOV" },
        { id: "Tidak rutin: NO inhalasi", en: "Not routine: inhaled NO" },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Ringkasan prinsip utama, bukan protokol ventilator lengkap (PEEP titration, driving pressure perlu penyesuaian individual).",
        en: "Summary of key principles, not a full ventilator protocol (PEEP titration, driving pressure need individual adjustment).",
      },
    },
  ],
};

const choking = {
  id: "choking",
  specialty: "respiratory",
  available: true,
  title: { id: "Sumbatan Jalan Napas Benda Asing (Choking)", en: "Foreign Body Airway Obstruction (Choking)" },
  desc: { id: "", en: "" },
  meta: {
    id: "Kerangka umum BLS, konsisten dengan AHA Guidelines 2025 · Direview terakhir: Jul 2026",
    en: "General BLS framework, consistent with AHA Guidelines 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Dewasa/anak besar, sadar, sumbatan berat (tak bisa bicara/batuk efektif, stridor, sianosis)?",
            en: "Adult/large child, conscious, severe obstruction (unable to speak/cough effectively, stridor, cyanosis)?",
          },
          bodyExtra: {
            id: "Ringan (masih batuk/bicara) → dorong batuk, jangan intervensi manual, awasi ketat.",
            en: "Mild (still coughing/speaking) → encourage cough, no manual intervention, monitor closely.",
          },
        },
        {
          badge: "2",
          body: {
            id: "Sumbatan berat, pasien sadar: kombinasi 5 back blows bergantian dengan 5 abdominal thrusts (Heimlich), ulangi hingga benda keluar atau pasien tidak sadar.",
            en: "Severe obstruction, conscious patient: alternate 5 back blows with 5 abdominal thrusts (Heimlich), repeat until object is expelled or patient becomes unconscious.",
          },
        },
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: { id: "Pasien menjadi tidak sadar?", en: "Patient becomes unconscious?" },
          bodyExtra: {
            id: "Ya → baringkan, mulai RJP. Setiap membuka jalan napas untuk napas bantuan, periksa mulut, keluarkan benda bila terlihat (jangan sapuan jari buta).",
            en: "Yes → lay down, begin CPR. Each time you open the airway for rescue breaths, inspect the mouth and remove the object if visible (no blind finger sweep).",
          },
        },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Pada bayi <1 tahun, teknik berbeda (back blows + chest thrusts, bukan abdominal thrusts) — algoritma terpisah di section Pediatric.",
        en: "For infants <1 year, technique differs (back blows + chest thrusts, not abdominal thrusts) — separate algorithm in the Pediatric section.",
      },
    },
  ],
};

const vent = {
  id: "vent",
  specialty: "respiratory",
  available: true,
  title: { id: "Setting Awal Ventilator & Kesiapan Weaning", en: "Initial Ventilator Setup & Weaning Readiness" },
  desc: { id: "", en: "" },
  meta: {
    id: "Prinsip ventilasi protektif (ARDSnet-based) + kriteria weaning standar · Direview terakhir: Jul 2026",
    en: "Protective ventilation principles (ARDSnet-based) + standard weaning criteria · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "panel",
      heading: { id: "Setting Awal", en: "Initial Setup" },
      table: {
        headers: { id: ["Parameter", "Nilai Awal"], en: ["Parameter", "Initial value"] },
        rows: [
          [
            { id: "Volume tidal", en: "Tidal volume" },
            {
              id: "6 mL/kg berat badan prediksi (4–8 mL/kg bila ARDS)",
              en: "6 mL/kg predicted body weight (4–8 mL/kg if ARDS)",
            },
          ],
          [
            { id: "Laju napas", en: "Respiratory rate" },
            { id: "14–20x/menit, sesuaikan target pCO2/pH", en: "14–20/min, adjust to target pCO₂/pH" },
          ],
          [
            { id: "FiO2", en: "FiO₂" },
            {
              id: "Mulai 100%, titrasi turun cepat sesuai SpO2/AGD",
              en: "Start 100%, wean down rapidly based on SpO₂/ABG",
            },
          ],
          [
            { id: "PEEP", en: "PEEP" },
            {
              id: "Minimal 5 cmH2O, titrasi sesuai oksigenasi & compliance",
              en: "Minimum 5 cmH₂O, titrate to oxygenation & compliance",
            },
          ],
          [
            { id: "Tekanan plateau", en: "Plateau pressure" },
            { id: "Batasi <30 cmH2O", en: "Keep <30 cmH₂O" },
          ],
        ],
      },
    },
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          decision: true,
          eyebrow: { id: "Kesiapan Weaning", en: "Weaning Readiness" },
          body: {
            id: "Kriteria terpenuhi? (penyebab gagal napas membaik, oksigenasi adekuat, hemodinamik stabil, mampu memulai napas spontan)",
            en: "Criteria met? (underlying cause improved, adequate oxygenation, haemodynamically stable, capable of spontaneous breathing)",
          },
          bodyExtra: {
            id: "Ya → lakukan Spontaneous Breathing Trial (SBT) 30–120 menit.",
            en: "Yes → perform Spontaneous Breathing Trial (SBT) 30–120 min.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "SBT berhasil (RR/tidal volume adekuat, tidak distres, hemodinamik stabil)?",
            en: "SBT successful (adequate RR/tidal volume, no distress, haemodynamically stable)?",
          },
          bodyExtra: {
            id: "Ya → pertimbangkan ekstubasi (nilai juga kemampuan proteksi jalan napas & batuk efektif). Tidak → kembali ke ventilasi suportif, cari penyebab kegagalan, ulangi esok hari.",
            en: "Yes → consider extubation (also assess airway protection & effective cough). No → return to supportive ventilation, identify cause of failure, retry the next day.",
          },
        },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Protokol weaning detail (rapid shallow breathing index, dll.) bisa ditambahkan sebagai kalkulator terpisah di menu Medical Calculator.",
        en: "Detailed weaning protocols (rapid shallow breathing index, etc.) can be added as separate calculators in the Medical Calculator menu.",
      },
    },
  ],
};

export const RESPIRATORY_ALGOS = { arf, asma, ppok, ards, choking, vent };
